import { useQuery } from '@tanstack/react-query';
import { BookHeart } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import {
  FineStatusBadge,
  LoanStatusBadge,
  MemberStatusBadge,
  Money,
  Stat,
  overdueText,
} from '@/features/library/shared';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate } from '@/utils/format';
import { PortalShell, type PortalContext } from './PortalShell';
import { SectionTitle } from './shared';

/**
 * The student's library account: membership, books out, and fines. Read-only —
 * borrowing and reserving happen in the reader's own library page.
 */
export function PortalLibraryPage() {
  return (
    <PortalShell title="Library account" icon={<BookHeart className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <LibraryAccount context={context} />}
    </PortalShell>
  );
}

function LibraryAccount({ context }: { context: PortalContext }) {
  const { subject, subjectKey } = context;

  const library = useQuery({
    queryKey: queryKeys.portal.library(subjectKey),
    queryFn: () => portalApi.library(subject),
  });

  if (library.isPending) {
    return (
      <Card>
        <LoadingState label="Loading the library account" />
      </Card>
    );
  }
  if (library.isError) {
    return (
      <Card>
        <ErrorState error={library.error} onRetry={() => void library.refetch()} />
      </Card>
    );
  }

  const { member, loans, fines } = library.data;

  if (member === undefined) {
    return (
      <Card>
        <EmptyState
          icon={<BookHeart className="h-6 w-6" aria-hidden="true" />}
          title="Not a library member"
          description="There is no library membership on record. The library can set one up."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Membership"
          value={<span className="text-[18px]">{member.membershipNumber}</span>}
          hint={member.libraryName}
        />
        <Stat
          label="Books out"
          value={`${member.booksOut} of ${member.borrowingLimit}`}
          hint={`${member.loanDays} days at a time`}
        />
        <Stat
          label="Fines owed"
          value={<Money value={member.outstandingFines} />}
          tone={member.outstandingFines > 0 ? 'warning' : 'neutral'}
          hint={member.outstandingFines > 0 ? 'Payable with school fees' : 'Nothing owed'}
        />
        <Stat
          label="Status"
          value={<MemberStatusBadge status={member.status} />}
          hint={member.expiresOn !== undefined ? `Expires ${formatDate(member.expiresOn)}` : undefined}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionTitle>Books out</SectionTitle>
          {loans.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">Nothing out at the moment.</p>
          ) : (
            <ul className="divide-y divide-line">
              {loans.map((loan) => (
                <li key={loan.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">{loan.bookTitle}</span>
                    <span
                      className={`block text-[11px] ${
                        loan.overdue ? 'text-[color:var(--sms-danger)]' : 'text-subtle'
                      }`}
                    >
                      {loan.overdue ? overdueText(loan.daysOverdue) : `back by ${formatDate(loan.dueDate)}`}
                    </span>
                  </span>
                  <LoanStatusBadge status={loan.status} overdue={loan.overdue} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionTitle>Fines</SectionTitle>
          {fines.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">No fines.</p>
          ) : (
            <ul className="divide-y divide-line">
              {fines.map((fine) => (
                <li key={fine.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{fine.reason}</span>
                    <span className="block text-[11px] text-subtle">{formatDate(fine.chargedOn)}</span>
                  </span>
                  <Money value={fine.financeOutstanding ?? fine.outstanding} />
                  <FineStatusBadge status={fine.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
